import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { SelectedFolderContext } from '../../context/selectedFolderContext';
import { AuthContext } from '../../context/authContext';
import ajaxActions from '../../services/ajaxActions';
import {
  RCM_COLLECTIONS,
  getCollectionById,
  getDefaultCollectionId,
} from '../../config/rcmCollections';
import {
  parseDeepLinkParams,
  applyDeepLinkSearch,
  hasDeepLinkParams,
  buildDeepLinkWarningMessages,
} from '../../utils/deepLinkParams';
import {
  loadRcmCaseIndex,
  resolveDeepLinkTarget,
  findPatientFolder,
  findLesionFolder,
  resolveCollectionFromParams,
  lookupCaseIndexEntry,
  getCaseBreadcrumbIds,
  caseFolderMatchesLesionId,
} from '../../utils/rcmCaseIndex';
import './Header.css';

function getPatientLabel(folder) {
  if (!folder) return 'Unknown';
  return folder.meta?.patient_id || folder.meta?.isic_id || folder.meta?.macroscopic_isic_id || folder.name || 'Unknown';
}

function getLesionLabel(folder) {
  if (!folder) return 'Unknown';
  return folder.meta?.lesion_id || folder.name || 'Unknown';
}

function hasRegistrationData(folder) {
  return Boolean(folder?.meta?.registrationData);
}

function formatLoginError(error, loginMode) {
  const message = error?.message || 'Login failed. Check your credentials and try again.';

  if (loginMode === 'password' && message === 'Login failed.') {
    return 'Username/password login was rejected by the DSA server. Use the API key tab instead — create a key under My account → API keys in the DSA web UI.';
  }

  if (loginMode === 'apiKey' && message === 'Invalid API key.') {
    return 'Invalid API key. Copy the full key from DSA (My account → API keys). Keys are long random strings, not your username or password.';
  }

  return message;
}

function getUserDisplayName(user) {
  if (!user) return '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.login || user.email || 'Logged in';
}

const Header = () => {
  const { setSelectedFolder } = useContext(SelectedFolderContext);
  const { currentUser, isLoggedIn, loginWithPassword, loginWithApiKey, logout } = useContext(AuthContext);

  const initialUrlParams = useMemo(
    () => parseDeepLinkParams(window.location.search),
    []
  );
  const initialCollectionId =
    resolveCollectionFromParams(initialUrlParams) ?? getDefaultCollectionId();

  const [selectedCollectionId, setSelectedCollectionId] = useState(initialCollectionId);
  const [patients, setPatients] = useState([]);
  const [lesions, setLesions] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [selectedLesionId, setSelectedLesionId] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingLesions, setLoadingLesions] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMode, setLoginMode] = useState('apiKey');
  const [loginCredentials, setLoginCredentials] = useState({ username: '', password: '', apiKey: '' });
  const [loginError, setLoginError] = useState('');
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [deepLinkTarget, setDeepLinkTarget] = useState(null);
  const [rcmCaseIndex, setRcmCaseIndex] = useState(null);
  const [indexReady, setIndexReady] = useState(false);
  const [indexAvailable, setIndexAvailable] = useState(true);
  const [deepLinkWarning, setDeepLinkWarning] = useState(null);

  const deepLinkAppliedRef = useRef(false);
  const deepLinkWarningEvaluatedRef = useRef(false);
  const preserveIsicUrlRef = useRef(Boolean(initialUrlParams.isicId));

  const rootFolderId = getCollectionById(selectedCollectionId).folderId;

  const selectCaseFolder = useCallback(
    (folder) => {
      setSelectedFolder(folder ?? null);
    },
    [setSelectedFolder]
  );

  useEffect(() => {
    let cancelled = false;

    loadRcmCaseIndex()
      .then((index) => {
        if (cancelled) return;
        const target = resolveDeepLinkTarget(index, initialUrlParams);
        setDeepLinkTarget(target);
        setRcmCaseIndex(index);
        setIndexReady(true);
        if (target) {
          console.log('Deep link resolved:', target, 'from', initialUrlParams);
        } else if (hasDeepLinkParams(initialUrlParams)) {
          console.warn('Deep link params did not resolve:', initialUrlParams);
        }
      })
      .catch((error) => {
        console.warn('RCM case index unavailable; deep links disabled.', error);
        if (!cancelled) {
          setIndexAvailable(false);
          setIndexReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialUrlParams]);

  useEffect(() => {
    let cancelled = false;

    const fetchPatients = async () => {
      setLoadingPatients(true);
      setSelectedPatientId('');
      setSelectedLesionId('');
      setLesions([]);
      selectCaseFolder(null);
      deepLinkAppliedRef.current = false;
      deepLinkWarningEvaluatedRef.current = false;
      setDeepLinkWarning(null);

      try {
        const subFolders = await ajaxActions.getSubFolders('folder', rootFolderId);
        if (cancelled) return;
        const sorted = [...(subFolders ?? [])].sort((a, b) =>
          getPatientLabel(a).localeCompare(getPatientLabel(b))
        );
        setPatients(sorted);
      } catch (error) {
        console.error('Error fetching patients:', error);
        if (!cancelled) setPatients([]);
      } finally {
        if (!cancelled) setLoadingPatients(false);
      }
    };

    fetchPatients();
    return () => {
      cancelled = true;
    };
  }, [rootFolderId, selectCaseFolder]);

  useEffect(() => {
    if (!indexReady || loadingPatients || patients.length === 0) {
      return;
    }

    if (deepLinkTarget?.patientId && !deepLinkAppliedRef.current) {
      const patient = findPatientFolder(patients, deepLinkTarget.patientId);
      if (patient) {
        setSelectedPatientId(patient._id);
        return;
      }
      console.warn('Deep link patient not found on DSA:', deepLinkTarget.patientId);
    }

    if (!selectedPatientId) {
      setSelectedPatientId(patients[0]._id);
    }
  }, [indexReady, loadingPatients, patients, deepLinkTarget, selectedPatientId]);

  useEffect(() => {
    if (!selectedPatientId) {
      setLesions([]);
      setSelectedLesionId('');
      return;
    }

    const patient = patients.find((p) => p._id === selectedPatientId);
    if (!patient) return;

    let cancelled = false;

    const fetchLesions = async () => {
      setLoadingLesions(true);
      try {
        const subFolders = await ajaxActions.getSubFolders('folder', patient._id);
        if (cancelled) return;

        const sorted = [...(subFolders ?? [])].sort((a, b) =>
          getLesionLabel(a).localeCompare(getLesionLabel(b))
        );

        if (sorted.length === 0 && hasRegistrationData(patient)) {
          setLesions([]);
          setSelectedLesionId('');
          selectCaseFolder(patient);
          deepLinkAppliedRef.current = true;
          return;
        }

        setLesions(sorted);

        const preferredLesionId =
          !deepLinkAppliedRef.current && deepLinkTarget?.lesionId
            ? deepLinkTarget.lesionId
            : null;
        const preferredLesion = preferredLesionId
          ? findLesionFolder(sorted, preferredLesionId)
          : null;

        if (preferredLesion) {
          setSelectedLesionId(preferredLesion._id);
          selectCaseFolder(preferredLesion);
          deepLinkAppliedRef.current = true;
        } else if (sorted.length === 1) {
          setSelectedLesionId(sorted[0]._id);
          selectCaseFolder(sorted[0]);
          deepLinkAppliedRef.current = true;
        } else if (sorted.length > 1) {
          if (!deepLinkAppliedRef.current && deepLinkTarget?.lesionId) {
            console.warn('Deep link lesion not found on DSA:', deepLinkTarget.lesionId);
          }
          setSelectedLesionId(sorted[0]._id);
          selectCaseFolder(sorted[0]);
          deepLinkAppliedRef.current = true;
        } else {
          setSelectedLesionId('');
          selectCaseFolder(null);
        }
      } catch (error) {
        console.error('Error fetching lesions:', error);
        if (!cancelled) {
          setLesions([]);
          setSelectedLesionId('');
        }
      } finally {
        if (!cancelled) setLoadingLesions(false);
      }
    };

    fetchLesions();
    return () => {
      cancelled = true;
    };
  }, [selectedPatientId, patients, selectCaseFolder, deepLinkTarget]);

  useEffect(() => {
    if (!indexReady || loadingPatients || loadingLesions) return;
    if (!hasDeepLinkParams(initialUrlParams)) return;
    if (deepLinkWarningEvaluatedRef.current) return;

    deepLinkWarningEvaluatedRef.current = true;

    const patientFolder = deepLinkTarget?.patientId
      ? findPatientFolder(patients, deepLinkTarget.patientId)
      : null;
    const lesionFolder = deepLinkTarget?.lesionId
      ? findLesionFolder(lesions, deepLinkTarget.lesionId)
      : null;
    const lesionOnPatientFolder =
      !lesionFolder
      && deepLinkTarget?.lesionId
      && patientFolder
      && lesions.length === 0
      && caseFolderMatchesLesionId(
        patientFolder,
        deepLinkTarget.lesionId,
        lookupCaseIndexEntry(
          rcmCaseIndex,
          deepLinkTarget.patientId,
          deepLinkTarget.lesionId
        )
      );

    const messages = buildDeepLinkWarningMessages({
      params: initialUrlParams,
      target: deepLinkTarget,
      indexAvailable,
      patientFound: deepLinkTarget?.patientId ? Boolean(patientFolder) : undefined,
      lesionFound: deepLinkTarget?.lesionId
        ? Boolean(lesionFolder || lesionOnPatientFolder)
        : undefined,
    });

    if (messages.length > 0) {
      setDeepLinkWarning(messages.join(' '));
    }
  }, [
    indexReady,
    indexAvailable,
    loadingPatients,
    loadingLesions,
    initialUrlParams,
    deepLinkTarget,
    patients,
    lesions,
    rcmCaseIndex,
  ]);

  useEffect(() => {
    if (loadingPatients || loadingLesions || !selectedPatientId) {
      return;
    }

    if (preserveIsicUrlRef.current && initialUrlParams.isicId) {
      return;
    }

    const patient = patients.find((p) => p._id === selectedPatientId);
    if (!patient) return;

    const lesion = lesions.find((l) => l._id === selectedLesionId);
    const patientKey = patient.meta?.patient_id || patient.name;
    const lesionKeyFromFolder = lesion?.meta?.lesion_id || lesion?.name;
    const caseFolder = lesion ?? (hasRegistrationData(patient) ? patient : null);
    const indexEntry = lookupCaseIndexEntry(rcmCaseIndex, patientKey, lesionKeyFromFolder);
    const { lesionId: lesionKey } = getCaseBreadcrumbIds(caseFolder, indexEntry);

    applyDeepLinkSearch({
      patientId: patientKey,
      lesionId: lesionKey,
      collection: selectedCollectionId,
    });
  }, [
    selectedCollectionId,
    selectedPatientId,
    selectedLesionId,
    patients,
    lesions,
    loadingPatients,
    loadingLesions,
    initialUrlParams.isicId,
    rcmCaseIndex,
  ]);

  const handleCollectionChange = (event) => {
    preserveIsicUrlRef.current = false;
    deepLinkWarningEvaluatedRef.current = false;
    setDeepLinkWarning(null);
    setSelectedCollectionId(event.target.value);
  };

  const handlePatientChange = (event) => {
    preserveIsicUrlRef.current = false;
    setDeepLinkWarning(null);
    setSelectedPatientId(event.target.value);
    setSelectedLesionId('');
  };

  const handleLesionChange = (event) => {
    preserveIsicUrlRef.current = false;
    setDeepLinkWarning(null);
    const lesionId = event.target.value;
    const lesion = lesions.find((l) => l._id === lesionId);
    setSelectedLesionId(lesionId);
    selectCaseFolder(lesion ?? null);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError('');
    setLoginSubmitting(true);
    try {
      if (loginMode === 'apiKey') {
        await loginWithApiKey(loginCredentials.apiKey);
      } else {
        await loginWithPassword(loginCredentials.username, loginCredentials.password);
      }
      setShowLoginModal(false);
      setLoginCredentials({ username: '', password: '', apiKey: '' });
    } catch (error) {
      console.error('Login failed:', error);
      setLoginError(formatLoginError(error, loginMode));
    } finally {
      setLoginSubmitting(false);
    }
  };

  const openLoginModal = () => {
    setLoginError('');
    setShowLoginModal(true);
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
    setLoginError('');
    setLoginCredentials({ username: '', password: '', apiKey: '' });
  };

  const switchLoginMode = (mode) => {
    setLoginMode(mode);
    setLoginError('');
  };

  const handleLogout = () => {
    logout();
  };
  const showLesionDropdown = lesions.length > 1;

  const currentCaseFolder = useMemo(() => {
    if (!selectedPatientId) return null;

    if (selectedLesionId) {
      return lesions.find((lesion) => lesion._id === selectedLesionId) ?? null;
    }

    const patient = patients.find((entry) => entry._id === selectedPatientId);
    if (patient && (hasRegistrationData(patient) || lesions.length === 0)) {
      return patient;
    }

    return null;
  }, [selectedPatientId, selectedLesionId, patients, lesions]);

  const caseBreadcrumbs = useMemo(() => {
    if (loadingPatients || loadingLesions || !currentCaseFolder) {
      return { lesionId: null, rcmCaseId: null };
    }

    const patient = patients.find((entry) => entry._id === selectedPatientId);
    const lesion = lesions.find((entry) => entry._id === selectedLesionId);
    const patientKey = patient?.meta?.patient_id || patient?.name;
    const lesionKey = lesion?.meta?.lesion_id || lesion?.name;
    const indexEntry = lookupCaseIndexEntry(rcmCaseIndex, patientKey, lesionKey);

    return getCaseBreadcrumbIds(currentCaseFolder, indexEntry);
  }, [
    loadingPatients,
    loadingLesions,
    currentCaseFolder,
    patients,
    lesions,
    selectedPatientId,
    selectedLesionId,
    rcmCaseIndex,
  ]);

  const showCaseBreadcrumbs = Boolean(caseBreadcrumbs.lesionId || caseBreadcrumbs.rcmCaseId);

  return (
    <div className="header-wrapper">
      <div className="header">
        <div className="header-content">
        <div className="header-logo">RCM Viewer</div>

        <div className="header-dropdown">
          <label className="header-select-label" htmlFor="collection-select">
            Collection
          </label>
          <select
            id="collection-select"
            value={selectedCollectionId}
            onChange={handleCollectionChange}
            className="folder-select collection-select"
          >
            {RCM_COLLECTIONS.map((collection) => (
              <option key={collection.id} value={collection.id}>
                {collection.label}
              </option>
            ))}
          </select>
        </div>

        <div className="header-dropdown">
          <label className="header-select-label" htmlFor="patient-select">
            Patient
          </label>
          <select
            id="patient-select"
            value={selectedPatientId}
            onChange={handlePatientChange}
            className="folder-select patient-select"
            disabled={loadingPatients || patients.length === 0}
          >
            {loadingPatients ? (
              <option value="">Loading patients...</option>
            ) : patients.length === 0 ? (
              <option value="">No patients found</option>
            ) : (
              patients.map((patient) => (
                <option
                  key={patient._id}
                  value={patient._id}
                  title={patient.name}
                >
                  {getPatientLabel(patient)}
                </option>
              ))
            )}
          </select>
          {showCaseBreadcrumbs && (
            <nav className="header-breadcrumbs" aria-label="Current case">
              {caseBreadcrumbs.lesionId && (
                <>
                  <span className="header-breadcrumbs__sep" aria-hidden="true">
                    /
                  </span>
                  <span className="header-breadcrumbs__item" title="Lesion ID">
                    {caseBreadcrumbs.lesionId}
                  </span>
                </>
              )}
              {caseBreadcrumbs.rcmCaseId && (
                <>
                  <span className="header-breadcrumbs__sep" aria-hidden="true">
                    /
                  </span>
                  <span className="header-breadcrumbs__item" title="RCM case ID">
                    {caseBreadcrumbs.rcmCaseId}
                  </span>
                </>
              )}
            </nav>
          )}
        </div>

        {showLesionDropdown && (
          <div className="header-dropdown">
            <label className="header-select-label" htmlFor="lesion-select">
              Lesion
            </label>
            <select
              id="lesion-select"
              value={selectedLesionId}
              onChange={handleLesionChange}
              className="folder-select"
              disabled={loadingLesions || lesions.length === 0}
            >
              {loadingLesions ? (
                <option value="">Loading lesions...</option>
              ) : (
                lesions.map((lesion) => (
                  <option
                    key={lesion._id}
                    value={lesion._id}
                    title={lesion.name}
                  >
                    {getLesionLabel(lesion)}
                  </option>
                ))
              )}
            </select>
          </div>
        )}

        <div className="header-spacer" />

        <div className="header-user-panel">
          <div className="user-info">
            {isLoggedIn ? (
              <>
                <span className="user-name" title={currentUser?.login}>
                  {getUserDisplayName(currentUser)}
                </span>
                <button type="button" className="logout-button" onClick={handleLogout}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <span className="user-name">Public access</span>
                <button type="button" className="login-button" onClick={openLoginModal}>
                  Log in
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      </div>

      {deepLinkWarning && (
        <div className="deep-link-warning" role="alert">
          <span className="deep-link-warning__text">{deepLinkWarning}</span>
          <button
            type="button"
            className="deep-link-warning__dismiss"
            onClick={() => setDeepLinkWarning(null)}
            aria-label="Dismiss warning"
          >
            ×
          </button>
        </div>
      )}

      {showLoginModal && (
        <div className="login-modal-overlay" onClick={closeLoginModal}>
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="login-modal-header">
              <h3>Login</h3>
              <button className="close-button" onClick={closeLoginModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleLogin} className="login-form">
              {loginError && (
                <p className="login-form-error" role="alert">
                  {loginError}
                </p>
              )}
              <div className="login-mode-toggle" role="tablist" aria-label="Login method">
                <button
                  type="button"
                  role="tab"
                  aria-selected={loginMode === 'apiKey'}
                  className={`login-mode-btn${loginMode === 'apiKey' ? ' active' : ''}`}
                  onClick={() => switchLoginMode('apiKey')}
                  disabled={loginSubmitting}
                >
                  API key
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={loginMode === 'password'}
                  className={`login-mode-btn${loginMode === 'password' ? ' active' : ''}`}
                  onClick={() => switchLoginMode('password')}
                  disabled={loginSubmitting}
                >
                  Username &amp; password
                </button>
              </div>
              {loginMode === 'apiKey' ? (
                <div className="login-form-field">
                  <label htmlFor="api-key">DSA API key</label>
                  <input
                    type="password"
                    id="api-key"
                    className="login-form-input"
                    autoComplete="off"
                    value={loginCredentials.apiKey}
                    onChange={(e) =>
                      setLoginCredentials({ ...loginCredentials, apiKey: e.target.value })
                    }
                    required
                    disabled={loginSubmitting}
                  />
                </div>
              ) : (
                <>
                  <div className="login-form-field">
                    <label htmlFor="username">Username</label>
                    <input
                      type="text"
                      id="username"
                      className="login-form-input"
                      autoComplete="username"
                      value={loginCredentials.username}
                      onChange={(e) =>
                        setLoginCredentials({ ...loginCredentials, username: e.target.value })
                      }
                      required
                      disabled={loginSubmitting}
                    />
                  </div>
                  <div className="login-form-field">
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      id="password"
                      className="login-form-input"
                      autoComplete="current-password"
                      value={loginCredentials.password}
                      onChange={(e) =>
                        setLoginCredentials({ ...loginCredentials, password: e.target.value })
                      }
                      required
                      disabled={loginSubmitting}
                    />
                  </div>
                </>
              )}
              <p className="login-form-hint">
                {loginMode === 'apiKey'
                  ? 'Use a DSA API key from your account page (My account → API keys). Recommended for saving co-registration data to the server.'
                  : 'Sign in with your DSA username and password. Some deployments disable password login and require an API key instead.'}
              </p>
              <div className="login-form-actions">
                <button
                  type="button"
                  className="login-form-btn login-form-btn--secondary"
                  onClick={closeLoginModal}
                  disabled={loginSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="login-form-btn login-form-btn--primary"
                  disabled={loginSubmitting}
                >
                  {loginSubmitting ? 'Logging in…' : 'Log in'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
